// DriverQRModal — iter77.
// Renders an opaque #171717 bottom sheet with a large 220×220 QR code
// pointing at https://www.google.com/maps?q={lat},{lng}. Designed for the
// traveler to show their driver from inside an offline-prone city.
//
// Uses the existing `qrcode.react` library (already a dep — see VendorPublic,
// MyQr, BusinessLayout, BusinessQR).
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Sun, WifiOff, Download, Map, MapPin } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useI18n } from "../lib/i18n";

/* Best-effort iOS detector — used to gate the Apple Maps link. */
function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export default function DriverQRModal({ business, open, onClose, testidPrefix = "driver-qr" }) {
  const { t } = useI18n();
  const qrWrapRef = useRef(null);

  // Lock body scroll + close on Esc while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open || !business) return null;

  const hasCoords = business.lat != null && business.lng != null;
  // Prefer plain google.com/maps?q= URL — it works in Google Maps app, the
  // web fallback, and any QR scanner. Maps coords are precise enough for
  // a driver pickup.
  const qrValue = hasCoords
    ? `https://www.google.com/maps?q=${business.lat},${business.lng}`
    : (business.google_maps_url || `https://www.google.com/maps?q=${encodeURIComponent(business.name || "")}`);

  const subParts = [business.category, business.neighborhood].filter(Boolean).join(" · ");

  const modal = (
    <div
      className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
      data-testid={`${testidPrefix}-sheet`}
      role="dialog"
      aria-label="Driver QR"
    >
      <div
        className="w-full sm:max-w-sm"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.3)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "10px 20px 24px",
          boxShadow: "0 -12px 32px rgba(0,0,0,0.55)",
        }}
      >
        {/* Handle bar */}
        <div className="mx-auto mb-3" style={{ width: 40, height: 3, borderRadius: 999, background: "#6a6a6a" }} />

        {/* Title + close */}
        <div className="flex items-start justify-between mb-4">
          <h2
            data-testid={`${testidPrefix}-title`}
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8", lineHeight: 1.2 }}
          >
            {t("driver_qr_title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testidPrefix}-close`}
            aria-label="Close"
            className="grid place-items-center rounded-full"
            style={{ minWidth: 36, minHeight: 36, color: "#B0AAA3", background: "transparent", border: "none" }}
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* QR — tappable, opens Google Maps. Gold pulse signals tappability. */}
        <a
          href={qrValue}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testidPrefix}-code`}
          className="block grid place-items-center"
          aria-label="Open in Google Maps"
        >
          <div ref={qrWrapRef} className="qr-gold-pulse" style={{ background: "#FFFFFF", padding: 14, borderRadius: 14, border: "2px solid rgba(232,184,75,0.5)" }}>
            <QRCodeSVG value={qrValue} size={220} level="M" includeMargin={false} fgColor="#0B0B0B" bgColor="#FFFFFF" />
          </div>
        </a>
        <p className="text-center mt-3" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}>{t("ride_qr_tap_caption")}</p>

        {/* Apple Maps + Google Maps direct links — thin gold divider between them.
            Apple Maps only on iOS. */}
        {business.lat != null && business.lng != null && (
          <div className="flex items-center justify-center gap-3 mt-2" data-testid={`${testidPrefix}-map-links`}>
            {isIos() && (
              <>
                <a
                  href={`maps://maps.apple.com/?q=${business.lat},${business.lng}`}
                  data-testid={`${testidPrefix}-apple-maps`}
                  className="inline-flex items-center gap-1.5"
                  style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 13, textDecoration: "none" }}
                >
                  <Map className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t("ride_open_apple_maps")}
                </a>
                <span aria-hidden style={{ width: 1, height: 14, background: "rgba(232,184,75,0.3)" }} />
              </>
            )}
            <a
              href={`https://www.google.com/maps?q=${business.lat},${business.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testidPrefix}-google-maps`}
              className="inline-flex items-center gap-1.5"
              style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 13, textDecoration: "none" }}
            >
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("ride_open_google_maps")}
            </a>
          </div>
        )}

        {/* Business name + sub */}
        <div className="text-center mt-3">
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, color: "#F5F1E8" }}>
            {business.name}
          </div>
          {subParts && (
            <div className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#9A9490" }}>
              {subParts}
            </div>
          )}
        </div>

        {/* Offline note (amber) */}
        <div
          className="mt-4 flex items-start gap-2"
          style={{
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.3)",
            borderRadius: 10,
            padding: "10px 12px",
          }}
        >
          <WifiOff className="shrink-0 mt-0.5" style={{ width: 14, height: 14, color: "#fbbf24" }} strokeWidth={1.5} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#fbbf24", lineHeight: 1.45 }}>
            {t("driver_qr_offline_card")}
          </span>
        </div>

        {/* Brightness tip */}
        <div
          className="mt-2 flex items-start gap-2"
          style={{
            background: "rgba(245,158,11,0.05)",
            border: "1px solid rgba(245,158,11,0.2)",
            borderRadius: 10,
            padding: "8px 12px",
          }}
        >
          <Sun className="shrink-0 mt-0.5" style={{ width: 14, height: 14, color: "#fbbf24" }} strokeWidth={1.5} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#fbbf24", lineHeight: 1.4 }}>
            {t("driver_qr_brightness_tip")}
          </span>
        </div>

        {/* Save QR + Done */}
        <button
          type="button"
          data-testid={`${testidPrefix}-save`}
          onClick={() => saveSvgAsPng(qrWrapRef.current, business.name || "habano-driver-qr")}
          className="mt-4 w-full inline-flex items-center justify-center gap-2"
          style={{ minHeight: 44, background: "transparent", border: "1px solid rgba(232,184,75,0.5)", color: "#E8B84B", borderRadius: 12, fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          <Download className="w-4 h-4" strokeWidth={1.5} />
          {t("driver_qr_save")}
        </button>
        <button
          type="button"
          data-testid={`${testidPrefix}-done`}
          onClick={onClose}
          className="mt-2 w-full inline-flex items-center justify-center hover:opacity-90 transition-opacity"
          style={{
            minHeight: 48,
            background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
            color: "#0B0B0B",
            borderRadius: 12,
            border: "none",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          {t("driver_qr_done")}
        </button>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

/* Convert the inline QR <svg> to a PNG and trigger a download.
 * On iOS Safari (where camera-roll write is restricted), the browser will
 * prompt the user to long-press and save manually. This is the best UX
 * available without a native bridge. */
function saveSvgAsPng(container, name) {
  if (!container) return;
  const svg = container.querySelector("svg");
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const svgUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const size = 720;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0, size, size);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(name || "habano-driver-qr").replace(/\s+/g, "_")}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/png");
  };
  img.src = svgUrl;
}
